import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { SimpleOrderContext } from 'pages/order-flow/simple-order/simple-order-context';
import SampleMethod from 'components/lab-order/sample-method/sample-method.component';
import DeliveryDatePicker from 'components/shared/delivery-date-picker/delivery-date-picker.component';
import DeliveryTimeWarning from 'components/shared/delivery-time-warning/delivery-time-warning.component';
import { Organization } from 'models/organization';
import DeliveryClinicPicker from 'components/shared/delivery-clinic-picker/delivery-clinic-picker.component';
import { Sample } from 'models/order';
import { getPerfectDeliveryDate } from 'utils/disable-order-delivery-dates';
import { useParams } from 'react-router-dom';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

type DeliveryFilesStepProps = {
    onNextClick: () => void;
    onBackClick: () => void;
};
const DeliveryFilesStep: FC<DeliveryFilesStepProps> = ({ onNextClick, onBackClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order, setOrder } = useContext(SimpleOrderContext);
    const [isValid, setIsValid] = useState(false);
    const [calculatedDate, setCalculatedDate] = useState<Date>();
    const params = useParams<{ id: string }>();
    const currentUser = currentUserVar();

    useEffect(() => {
        if (order) {
            setIsValid(
                !!order.deliveryDetails.destination?._id &&
                    !!order.deliveryDetails.pickUpLocation?._id &&
                    !!order.sample.type,
            );
            return;
        }
        setIsValid(false);
    }, [order]);

    // Set perfect delivery date when lab and constructions are selected (only for new orders)
    useEffect(() => {
        if (!params.id && order?.supplier && order?.restorations?.length) {
            const perfectDate = getPerfectDeliveryDate(order.supplier, order.restorations);
            if (perfectDate && perfectDate !== order.deliveryDetails?.date) {
                setCalculatedDate(perfectDate);
                setOrder((prev) => ({
                    ...prev,
                    deliveryDetails: {
                        ...prev.deliveryDetails,
                        date: perfectDate,
                    },
                }));
            }
        }
    }, []);

    const handleDeliveryDateChange = (date: Date | null, hour: number | undefined, minute: number | undefined) => {
        setOrder((prev) => ({
            ...prev,
            deliveryDetails: {
                ...prev.deliveryDetails,
                date,
                time: {
                    hour: hour,
                    minute: minute,
                },
            },
        }));
    };

    const handleSampleSelection = (sample: Sample) => {
        setOrder((prev) => ({
            ...prev,
            sample,
        }));
    };

    const handlePickUpLocationChange = (clinic: Organization) => {
        setOrder((prev) => ({
            ...prev,
            deliveryDetails: {
                ...prev.deliveryDetails,
                pickUpLocation: clinic,
            },
        }));
    };

    const handleDestinationChange = (clinic: Organization) => {
        setOrder((prev) => ({
            ...prev,
            deliveryDetails: {
                ...prev.deliveryDetails,
                destination: clinic,
            },
        }));
    };

    return (
        <Fragment>
            {order ? (
                <Fragment>
                    <Box>
                        <Box mb={2}>
                            <Typography variant="h1">{translate('orders:DELIVERY')}</Typography>
                        </Box>
                        <Box mb={2}>
                            <Divider />
                        </Box>
                        <Box mb={4}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <DeliveryDatePicker
                                        onChange={handleDeliveryDateChange}
                                        date={order.deliveryDetails.date}
                                        calculatedDate={calculatedDate}
                                        hour={order.deliveryDetails.time.hour}
                                        minute={order.deliveryDetails.time.minute}
                                        lab={order?.supplier as Organization}
                                        dataTest="delivery-files-step.date-picker"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <DeliveryClinicPicker
                                        clinic={order.deliveryDetails.pickUpLocation}
                                        onClinicSelected={handlePickUpLocationChange}
                                        label={translate('orders:PICK_UP_LOCATION')}
                                        dataTest="delivery-files-step.pickup-location"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <DeliveryClinicPicker
                                        clinic={order.deliveryDetails.destination}
                                        onClinicSelected={handleDestinationChange}
                                        label={translate('orders:DELIVERY_LOCATION')}
                                        dataTest="delivery-files-step.delivery-location"
                                    />
                                </Grid>
                            </Grid>
                            {permissions.isDentist(currentUser.type) && (
                                <DeliveryTimeWarning
                                    hour={order.deliveryDetails.time.hour}
                                    dataTest="delivery-files-step.time-warning"
                                />
                            )}
                        </Box>
                        <Box mb={4}>
                            <Box>
                                <Typography variant="h3" mb={1}>
                                    {translate('orders:HOW_WAS_THE_SAMPLE_TAKEN')}
                                </Typography>
                            </Box>
                            <SampleMethod
                                handleSampleSelection={handleSampleSelection}
                                sample={order.sample}
                                dataTest="delivery-files-step.sample-method"
                            />
                        </Box>
                    </Box>
                    <Box display="flex" mt={2}>
                        <Box flexGrow={1} textAlign="left">
                            <Button type="button" onClick={onBackClick} data-test="delivery-files-step.back">
                                {translate('common:BACK')}
                            </Button>
                        </Box>
                        <Box>
                            <Button
                                disabled={!isValid}
                                type="button"
                                onClick={onNextClick}
                                variant="contained"
                                data-test="delivery-files-step.next"
                            >
                                {translate('common:NEXT_STEP')}
                            </Button>
                        </Box>
                    </Box>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default DeliveryFilesStep;
